import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Smartphone } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { StatusBadge } from '@/components/StatusBadge'
import {
  CAMP_STR as S,
  fetchPushHealth,
  fetchTokenHealthDetail,
  type PushHealth,
  type TokenHealthDetail,
} from './campaignsApi'

const KeyVals = ({ title, map }: { title: string; map: Record<string, number> }) => (
  <div className="rounded-lg border border-border-default bg-surface-2 p-4">
    <div className="mb-2 text-sm text-secondary">{title}</div>
    {Object.keys(map).length === 0 ? (
      <div className="text-sm text-tertiary">—</div>
    ) : (
      <ul className="space-y-1">
        {Object.entries(map).map(([k, n]) => (
          <li key={k} className="flex justify-between text-sm">
            <span className="text-secondary">{k}</span>
            <span className="font-mono text-primary">{n.toLocaleString()}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
)

/**
 * A15.18 — Push tokenlari salomatligi. REAL /admin/push/health (FCM sozlangan-mi,
 * provayder bo'yicha token soni, kampaniya holati) + DORMANT /admin/push/token-health
 * (yangi/eskirgan/o'lik token detali — fail-soft, chiqmasa yashiriladi).
 */
export function PushTokenHealth() {
  const { can } = useAuth()
  const [h, setH] = useState<PushHealth | null>(null)
  const [detail, setDetail] = useState<TokenHealthDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    Promise.all([fetchPushHealth(ac.signal), fetchTokenHealthDetail(ac.signal)])
      .then(([hh, dd]) => {
        if (!alive) return
        setH(hh)
        setDetail(dd)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  return (
    <>
      <PageHeader
        title={S.tokenTitle}
        screenId="A15.18"
        description={S.tokenNote}
        actions={
          <Link
            to="/broadcasts"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {S.back}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="grid max-w-3xl gap-3 sm:grid-cols-2">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-lg" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !h ? (
          <ErrorState title={S.empty} />
        ) : (
          <div className="max-w-3xl space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-border-default bg-surface-2 p-4">
                <div className="text-sm text-secondary">{S.fcm}</div>
                <div className="mt-2 flex items-center gap-2">
                  <StatusBadge status={h.fcm_configured ? 'active' : 'deleted'} label={h.fcm_configured ? S.fcmYes : S.fcmNo} />
                </div>
              </div>
              <div className="rounded-lg border border-border-default bg-surface-2 p-4">
                <div className="text-sm text-secondary">{S.pushTokens}</div>
                <div className="mt-1 text-2xl font-semibold text-primary">{h.push_tokens.toLocaleString()}</div>
              </div>
              <KeyVals title={S.byProvider} map={h.by_provider} />
              <KeyVals title={S.statusBreakdown} map={h.broadcast_status} />
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-tertiary">
                <Smartphone size={13} />
                {S.tokenTitle}
              </div>
              {detail ? (
                <div className="grid gap-3 sm:grid-cols-4">
                  <div className="rounded-lg border border-border-default bg-surface-2 p-4">
                    <div className="text-sm text-secondary">{S.fresh}</div>
                    <div className="mt-1 text-xl font-semibold text-success">{detail.fresh.toLocaleString()}</div>
                  </div>
                  <div className="rounded-lg border border-border-default bg-surface-2 p-4">
                    <div className="text-sm text-secondary">{S.stale}</div>
                    <div className="mt-1 text-xl font-semibold text-warning">{detail.stale.toLocaleString()}</div>
                  </div>
                  <div className="rounded-lg border border-border-default bg-surface-2 p-4">
                    <div className="text-sm text-secondary">{S.expired}</div>
                    <div className="mt-1 text-xl font-semibold text-danger">{detail.expired.toLocaleString()}</div>
                  </div>
                  <div className="rounded-lg border border-border-default bg-surface-2 p-4">
                    <div className="text-sm text-secondary">{S.invalid}</div>
                    <div className="mt-1 text-xl font-semibold text-danger">{detail.invalid.toLocaleString()}</div>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-border-default bg-surface-2 p-4 text-sm text-tertiary">
                  {S.freshnessDormant}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
